import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Wallet, Pencil, Lock } from "lucide-react";
import { SortableCardWrapper } from "./SortableCardWrapper";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";

interface AccountHorizontalScrollProps {
  accounts: any[];
  onAddAccount: () => void;
  onEditAccount: (account: any) => void;
  onAddTransaction: (accountId: string) => void;
  onDragEnd: (event: DragEndEvent) => void;
  formatCurrency: (value: number) => string;
  locked?: boolean;
}

export function AccountHorizontalScroll({
  accounts,
  onAddAccount,
  onEditAccount,
  onAddTransaction,
  onDragEnd,
  formatCurrency,
  locked = false,
}: AccountHorizontalScrollProps) {
  const navigate = useNavigate();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (accounts.length === 0) {
    return (
      <Card className="p-6 text-center">
        <Wallet className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
        <h3 className="text-base font-semibold mb-1">Nenhuma conta cadastrada</h3>
        <p className="text-sm text-muted-foreground mb-3">
          Comece criando sua primeira conta
        </p>
        <Button onClick={onAddAccount} size="sm">
          {locked ? (
            <Lock className="h-4 w-4 mr-2 text-muted-foreground" />
          ) : (
            <Plus className="h-4 w-4 mr-2" />
          )}
          Criar Primeira Conta
        </Button>
      </Card>
    );
  }

  return (
    <>
      {/* Mobile: horizontal scroll */}
      <div className="md:hidden flex overflow-x-auto snap-x snap-mandatory gap-3 pb-2 -mx-1 px-1">
        {accounts.map((account) => (
          <div key={account.id} className="snap-start shrink-0 w-[260px]">
            <Card className="overflow-hidden h-full">
              <div className="h-1.5" style={{ backgroundColor: account.color }} />
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium truncate">{account.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {account.type === "checking" && "Corrente"}
                    {account.type === "savings" && "Poupança"}
                    {account.type === "cash" && "Dinheiro"}
                    {account.type === "investment" && "Investimentos"}
                  </span>
                </div>
                <div className="text-xl font-bold mb-1">
                  {formatCurrency(Number(account.balance))}
                </div>
                {account.institution && (
                  <p className="text-xs text-muted-foreground">{account.institution}</p>
                )}
                <div className="flex gap-1.5 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 h-7 text-xs"
                    onClick={() => navigate(`/transactions?accountId=${account.id}`)}
                  >
                    Ver
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => onEditAccount(account)}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      {/* Desktop: grid with drag and drop */}
      <div className="hidden md:block">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={accounts.map(a => a.id)} strategy={rectSortingStrategy}>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {accounts.map((account) => (
                <SortableCardWrapper key={account.id} id={account.id}>
                  <Card className="overflow-hidden">
                    <div className="h-2" style={{ backgroundColor: account.color }} />
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center justify-between">
                        <span>{account.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground font-normal">
                            {account.type === "checking" && "Corrente"}
                            {account.type === "savings" && "Poupança"}
                            {account.type === "cash" && "Dinheiro"}
                            {account.type === "investment" && "Investimentos"}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => onEditAccount(account)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {formatCurrency(Number(account.balance))}
                      </div>
                      {account.institution && (
                        <p className="text-sm text-muted-foreground mt-1">{account.institution}</p>
                      )}
                      <div className="flex gap-1.5 mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onAddTransaction(account.id)}
                        >
                          <Plus className="h-4 w-4 mr-1.5" />
                          Nova
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => navigate(`/transactions?accountId=${account.id}`)}
                        >
                          <Wallet className="h-4 w-4 mr-2" />
                          Ver
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </SortableCardWrapper>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </>
  );
}
